<template>
  <div>
    <div
      class="flex justify-between items-end mb-5 mt-20 mdsm:mb-5 md:mt-16 sm:mt-15"
    >
      <h2 class="fs-24 font-medium" data-sticky-nav-item>Cabins</h2>

      <div class="gallery sm:hidden">
        <div class="lightbox-init">
          <div class="item" :data-src="cabinImages[0]">
            <button
              type="button"
              class="btn btn-secondary --small btn-outlined rounded font-medium input-normal"
              v-if="cabinImages.length"
            >
              View Vessel Deck plan
              <img
                class="hidden"
                :src="cabinImages[0]"
                alt="Image"
                width=""
                height=""
              />
            </button>
          </div>
          <div
            v-for="(image, index) in cabinImages.slice(1)"
            :key="index"
            class="item"
            :data-src="image"
            :data-index="index + 1"
          >
            <img
              class="hidden"
              :src="image"
              :alt="`Image ${index + 1}`"
              width=""
              height=""
            />
          </div>
        </div>
      </div>
    </div>
    <div>
      <ul
        class="mdsm:mt-0 cabin-grid list-reset"
        :class="{
          '--compact': [4, 7, 8].includes(cabins.length),
        }"
      >
        <li v-for="(cabin, index) in cabins" :key="cabin.id" class="cabin-item">
          <hr
            class="lg:hidden md:hidden border border-black-light-2 my-8 mdsm:my-6"
            v-if="index > 0"
          />
          <div class="flex flex-col resort-room sm:mt-6 sm:gap-3">
            <div class="w-full resort-room-image">
              <div class="aspect-ratio-20/13 h-full sm:h-auto">
                <div>
                  <cabin-image-carousel
                    class="cabin-image-liveaboard"
                    :images="cabin.images"
                  />
                </div>
              </div>
            </div>
            <div class="lex flex-col p-4 sm:p-0">
              <h4 class="fs-16 font-medium">{{ cabin.name }}</h4>

              <div class="mt-3 sm:mt-2">
                <div class="flex items-center mb-2 sm:mb-1">
                  <img
                    :src="cabin.bed_type_icon"
                    width="24"
                    height="24"
                    class="inline-block mr-3"
                  />
                  {{ cabin.bed_type }}
                </div>
                <div class="flex items-center mb-2 sm:mb-1">
                  <svg width="24" height="24" class="inline-block mr-3">
                    <use xlink:href="/img/sprite.svg#ic_people" />
                  </svg>
                  {{ cabin.occupancy }}
                  {{ cabin.occupancy > 1 ? "people" : "person" }}
                </div>
                <div class="flex items-center">
                  <svg width="24" height="24" class="inline-block mr-3">
                    <use xlink:href="/img/sprite.svg#ic_shareable" />
                  </svg>
                  {{ cabin.is_private ? "Non shareable" : "Shareable" }}
                </div>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <hr class="hidden border border-black-light-2 my-8 sm:hidden" />

      <div class="w-full">
        <div
          class="box is-blue is-solid wrap-box px-10 py-4 rounded border border-black-light-2 mt-10 sm:mt-8"
        >
          <ul class="list-color-dot">
            <li>
              If you are a solo traveller, we aim to allocate shared cabins on a
              same-sex basis.
            </li>
            <li>
              Non-shareable cabins can not be shared with another single
              traveler outside your party. A single occupancy surcharge will be
              applied.
            </li>
          </ul>
        </div>
      </div>

      <div class="gallery md:hidden lg:hidden">
        <div class="lightbox-init">
          <div class="item" :data-src="cabinImages[0]">
            <button
              type="button"
              class="btn btn-secondary btn-outlined rounded mt-8 sm:w-full font-medium input-normal"
              v-if="cabinImages.length"
            >
              View Vessel Deck plan
              <img
                class="hidden"
                :src="cabinImages[0]"
                alt="Image"
                width=""
                height=""
              />
            </button>
          </div>
          <div
            v-for="(image, index) in cabinImages.slice(1)"
            :key="index"
            class="item"
            :data-src="image"
            :data-index="index + 1"
          >
            <img
              class="hidden"
              :src="image"
              :alt="`Image ${index + 1}`"
              width=""
              height=""
            />
          </div>
        </div>
      </div>

      <zb-modal ref="roomDetailsModal" class="slide-in-modal">
        <h2 slot="title" class="fs-20 font-medium mb-6">Cabin amenities</h2>

        <template>
          <div v-if="viewingCabin">
            <template v-if="included.length">
              <h5 class="font-medium my-3" v-if="included.length">Included</h5>
              <inclusion-sub-list :inclusions="included" />
            </template>

            <template v-if="notIncluded.length">
              <hr class="border border-black-30 my-6" />
              <h5 class="font-medium my-3">Not Included</h5>
              <inclusion-sub-list :inclusions="notIncluded" />
            </template>

            <template v-if="extra.length">
              <hr class="border border-black-30 my-6" />
              <h5 class="font-medium my-3">Additional cost</h5>
              <inclusion-sub-list :inclusions="extra" />
            </template>
          </div>
        </template>
      </zb-modal>
    </div>
  </div>
</template>

<script>
import InclusionSubList from "../../components/core/InclusionSubList";
import CabinImageCarousel from "./CabinImageCarousel";
import ZbModal from "../../components/core/ZbModal";
import CurrencyContext from "../../components/core/CurrencyContext";

export default {
  components: {
    InclusionSubList,
    CabinImageCarousel,
    ZbModal,
    CurrencyContext,
  },
  props: ["resortId"],
  data() {
    return {
      cabins: [],
      viewingCabin: null,
      cabinImages: [],
    };
  },
  computed: {
    included() {
      return this.viewingCabin.inclusions.filter(
        (incl) => incl.type === "included"
      );
    },
    extra() {
      return this.viewingCabin.inclusions.filter(
        (incl) => incl.type === "extra"
      );
    },
    notIncluded() {
      return this.viewingCabin.inclusions.filter(
        (incl) => incl.type === "not_included"
      );
    },
  },
  mounted() {
    window
      .fetchSupplierInfo(this.resortId)
      .then((response) => {
        this.cabins = response.rooms;
        if (response.supplier.deck_plan) {
          this.cabinImages = [response.supplier.deck_plan];
        }
      })
      .catch(() => {
        this.cabins = [];
      });
  },
  watch: {
    cabins(cabins) {
      const numberOfCabins = cabins.reduce((agg, c) => {
        return agg + c.available_rooms;
      }, 0);

      $("#num_of_cabins").text(numberOfCabins);
    },
  },
};
</script>
<style scoped>
.resort-room-image {
  max-width: unset;
  width: 100%;
  flex: none !important;
  border-bottom-left-radius: 0px;
}

::v-deep .cabin-image-liveaboard > img {
  border-radius: 0.25rem 0.25rem 0 0;
}

@media (max-width: 600px) {
  ::v-deep .cabin-image-liveaboard > img {
    border-radius: 0.25rem;
    height: 100%;
    min-height: 110px;
  }
  .resort-room {
    display: flex;
    flex-direction: row;
    min-height: unset;
  }

  .resort-room-image {
    width: 151px;
  }
}

@media only screen and (min-width: 600px) {
  .resort-room {
    border: 1px solid var(--black-light-2);
    border-radius: 4px;
    max-height: initial;
  }

  .cabin-grid {
    row-gap: 2.5rem;
    display: grid;
    grid-template-columns: repeat(3, 350px);
    justify-content: space-between;
    align-items: flex-start;
  }

  .cabin-grid.--compact {
    grid-template-columns: repeat(4, 290px);
    row-gap: auto;
  }

  .cabin-item {
    height: 100%;
  }

  .cabin-item > .resort-room {
    height: 100%;
  }
}

@media (min-width: 600px) and (max-width: 1200px) {
  .cabin-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 32px;
  }
}
</style>
